import React, { useState, useEffect } from "react";
import {
  Box,
  styled,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  getUserTypes,
  UserType,
} from "@services/UserManagement";
import { getApiOrigin } from "@utils/apiOrigin";
import { formatUserTypeDisplayName } from "@utils/userUtils";

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    background: "#fdffff",
    borderRadius: "12px",
    border: "1px solid #99d4e1",
    boxShadow: "8px 2px 58.4px 0px inset rgba(191, 242, 255, 0.2)",
    padding: 0,
    maxWidth: "800px",
    width: "100%",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
});

const ModalContent = styled(DialogContent)({
  padding: "32px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const ModalHeader = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const ModalTitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "24px",
  fontWeight: 600,
  color: "#065b70",
  lineHeight: "normal",
});

const ModalSubtitle = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 400,
  color: "#065b70",
  lineHeight: "normal",
});

const FormSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const FormRow = styled(Box)({
  display: "flex",
  gap: "16px",
  width: "100%",
});

const FormField = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
});

const FieldLabel = styled(Typography)({
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
  color: "#065b70",
  lineHeight: "normal",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: "14px",
    "& input": {
      padding: "12px 16px",
      fontFamily: '"Rethink Sans", sans-serif',
      fontSize: "14px",
      color: "#262626",
      "&::placeholder": {
        color: "#a3a3a3",
        opacity: 1,
      },
    },
    "&:hover": {
      border: "1px solid #bfe5ec",
    },
    "&.Mui-focused": {
      border: "1px solid #0a829d",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const StyledSelect = styled(FormControl)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: "14px",
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontFamily: '"Rethink Sans", sans-serif',
      fontSize: "14px",
      color: "#262626",
      "&.MuiSelect-select[aria-expanded='false']": {
        color: "#a3a3a3",
      },
    },
    "&:hover": {
      border: "1px solid #bfe5ec",
    },
    "&.Mui-focused": {
      border: "1px solid #0a829d",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const ButtonRow = styled(Box)({
  display: "flex",
  gap: "12px",
  justifyContent: "center",
  marginTop: "8px",
});

const CancelButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#737373",
  border: "1px solid #e0e0e0",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    border: "1px solid #bfe5ec",
  },
});

const SaveButton = styled(Button)({
  background: "#0a829d",
  color: "white",
  fontFamily: '"Rethink Sans", sans-serif',
  fontSize: "14px",
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&:hover": {
    background: "#085f73",
  },
  "&:disabled": {
    background: "#b0c4c8",
    color: "#e6fafc",
  },
});

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    userType: string;
    userTypeId?: number;
    userSubtypeId?: number;
    associationId?: number;
  } | null;
  facilityId: number;
  onUserUpdated?: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  user,
  facilityId,
  onUserUpdated,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [loading, setLoading] = useState(false);
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [loadingUserTypes, setLoadingUserTypes] = useState(false);
  // Validation errors
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  // Populate form when user data is provided
  useEffect(() => {
    if (user && open) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
      setUserType(user.userType || "");
    }
  }, [user, open]);

  // Fetch user types when modal opens
  useEffect(() => {
    if (open) {
      const fetchUserTypes = async () => {
        try {
          setLoadingUserTypes(true);
          const response = await getUserTypes({
            page: 1,
            page_size: 20,
            filters: { is_active: true },
          });
          if (response.success && response.data.items) {
            setUserTypes(response.data.items);
          }
        } catch (error: any) {
          console.error("Error fetching user types:", error);
          toast.error("Failed to load user types.");
          setUserTypes([]);
        } finally {
          setLoadingUserTypes(false);
        }
      };
      fetchUserTypes();
    }
  }, [open]);

  const validateFirstName = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "First Name is required";
    return "";
  };

  const validateLastName = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "Last Name is required";
    return "";
  };

  const handleSubmit = async () => {
    // Run validation
    const firstErr = validateFirstName(firstName);
    const lastErr = validateLastName(lastName);
    setFirstNameError(firstErr);
    setLastNameError(lastErr);

    if (firstErr || lastErr || !user) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    try {
      setLoading(true);

      const baseUrl = getApiOrigin();
      if (!baseUrl) {
        throw new Error("API base URL is not configured.");
      }

      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Not authenticated. Please sign in again.");
      }

      const userId = user.id;
      const response = await fetch(
        `${baseUrl}/api/v1/user-management/user/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            first_name: trimmedFirst,
            last_name: trimmedLast,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to update user (${response.status})`
        );
      }

      toast.success("User updated successfully.");
      onClose();
      onUserUpdated?.();
    } catch (error) {
      console.error("Error updating user:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update user.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const isFormValid = Boolean(
    firstName.trim() &&
      lastName.trim() &&
      !firstNameError &&
      !lastNameError
  );

  if (!user) {
    return null;
  }

  return (
    <StyledDialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Edit User</ModalTitle>
          <ModalSubtitle>
            Update user information and settings
          </ModalSubtitle>
        </ModalHeader>

        <FormSection>
          <FormRow>
            <FormField>
              <FieldLabel>First Name</FieldLabel>
              <StyledTextField
                placeholder="Enter First Name"
                value={firstName}
                onChange={(e) => {
                  const value = e.target.value;
                  setFirstName(value);
                  if (firstNameError) {
                    setFirstNameError(validateFirstName(value));
                  }
                }}
                onBlur={(e) => setFirstNameError(validateFirstName(e.target.value))}
                error={!!firstNameError}
                helperText={firstNameError}
                required
                fullWidth
              />
            </FormField>

            <FormField>
              <FieldLabel>Last Name</FieldLabel>
              <StyledTextField
                placeholder="Enter Last Name"
                value={lastName}
                onChange={(e) => {
                  const value = e.target.value;
                  setLastName(value);
                  if (lastNameError) {
                    setLastNameError(validateLastName(value));
                  }
                }}
                onBlur={(e) => setLastNameError(validateLastName(e.target.value))}
                error={!!lastNameError}
                helperText={lastNameError}
                required
                fullWidth
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <FieldLabel>Email</FieldLabel>
              <StyledTextField
                placeholder="Enter Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f5f5",
                    "& input": {
                      color: "#737373",
                      cursor: "not-allowed",
                    },
                  },
                }}
              />
            </FormField>

            <FormField>
              <FieldLabel>User Type</FieldLabel>
              <StyledSelect 
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f5f5f5",
                    "& .MuiSelect-select": {
                      color: "#737373",
                      cursor: "not-allowed",
                    },
                  },
                }}
              >
                <Select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                  displayEmpty
                  disabled={true}
                >
                  <MenuItem value="" disabled>
                    {loadingUserTypes
                      ? "Loading user types..."
                      : userTypes.length === 0
                      ? "No user types available"
                      : "Select User Type"}
                  </MenuItem>
                  {userTypes.map((type) => (
                    <MenuItem key={type.id} value={type.name}>
                      {formatUserTypeDisplayName(type.name)}
                    </MenuItem>
                  ))}
                </Select>
              </StyledSelect>
            </FormField>
          </FormRow>
        </FormSection>

        <ButtonRow>
          <CancelButton onClick={handleCancel} disabled={loading}>
            Cancel
          </CancelButton>
          <SaveButton onClick={handleSubmit} disabled={!isFormValid || loading}>
            {loading && (
              <CircularProgress size={16} sx={{ color: "white" }} />
            )}
            {loading ? "Saving..." : "Save Changes"}
          </SaveButton>
        </ButtonRow>
      </ModalContent>
    </StyledDialog>
  );
};

export default EditUserModal;

